// Pure quote resolution over the transfer catalog — no drizzle, no nitro, so
// the public request endpoint (server-side recompute) and the vitest suite
// run the exact same rules. Conventions deliberately mirror apps/web's
// shared/quote.js: an in-season row beats the year-round fallback, and
// "nothing resolves" means a manual quote, never a guessed price.

export const ANY_VEHICLE = 0

export interface QuotablePlace {
    id: number
    areaId: number | null
    active: boolean
}

export interface QuotableRoute {
    id: number
    fromPlaceId: number
    toPlaceId: number
    bidirectional: boolean
    active: boolean
}

export interface QuotablePriceRow {
    id: number
    routeId: number
    vehicleTypeId: number
    tripType: 'one_way' | 'round_trip'
    kind: 'fixed' | 'included' | 'on_request'
    amount: number | null
    seasonStart: string
    seasonEnd: string
}

export interface QuotableVehicleType {
    id: number
    maxPassengers: number
    active: boolean
}

export interface QuoteCatalog {
    places: QuotablePlace[]
    routes: QuotableRoute[]
    routePrices: QuotablePriceRow[]
    vehicleTypes: QuotableVehicleType[]
}

export interface QuoteInput {
    fromPlaceId: number
    toPlaceId: number
    tripType: 'one_way' | 'round_trip'
    // 'YYYY-MM-DD' pickup date; '' skips seasonal rows (year-round only).
    date: string
    passengers: number
}

export interface TransferQuote {
    routeId: number
    priceId: number
    // The matched route runs opposite to the requested direction (allowed
    // only on bidirectional routes).
    reversed: boolean
    tripType: 'one_way' | 'round_trip'
    kind: 'fixed' | 'included' | 'on_request'
    // Vehicles needed for the group: 1 when a single vehicle seats everyone,
    // otherwise the identical-vehicle split the total is multiplied from.
    quantity: number
    // Per-vehicle price for the whole trip; null unless the row commits to a
    // number ('included' is 0).
    unitAmount: number | null
    // quantity × unitAmount — the total for the whole group.
    amount: number | null
    vehicleTypeId: number
    // Round trip priced as 2× the one-way row because no explicit (discounted)
    // round_trip row exists.
    derivedRoundTrip: boolean
}

// Endpoints match a route directly or through their area ("Nungwi" matches a
// route priced from "North Coast"). Direct pairs win over area-lifted ones,
// and any as-entered direction wins over a reversed bidirectional match.
export function matchTransferRoute(catalog: QuoteCatalog, fromPlaceId: number, toPlaceId: number) {
    const lift = (id: number) => {
        const place = catalog.places.find(p => p.id === id)
        return place?.areaId ? [id, place.areaId] : [id]
    }
    const fromIds = lift(fromPlaceId)
    const toIds = lift(toPlaceId)

    for (const from of fromIds) {
        for (const to of toIds) {
            const route = catalog.routes.find(r => r.active && r.fromPlaceId === from && r.toPlaceId === to)
            if (route)
                return { route, reversed: false }
        }
    }
    for (const from of fromIds) {
        for (const to of toIds) {
            const route = catalog.routes.find(r => r.active && r.bidirectional && r.fromPlaceId === to && r.toPlaceId === from)
            if (route)
                return { route, reversed: true }
        }
    }
    return null
}

// Same three-state season logic as shared/quote.js isRowInSeason /
// resolveSeasonalRow: a row inside its window wins, the year-round row
// ('' bounds) is the fallback, a seasonal row never matches a missing date.
function resolveSeasonalRow<T extends { seasonStart: string, seasonEnd: string }>(rows: T[], date: string): T | null {
    const seasonal = rows.find((row) => {
        if (!row.seasonStart && !row.seasonEnd)
            return false
        if (!date)
            return false
        return (!row.seasonStart || date >= row.seasonStart) && (!row.seasonEnd || date <= row.seasonEnd)
    })
    if (seasonal)
        return seasonal
    return rows.find(row => !row.seasonStart && !row.seasonEnd) ?? null
}

// Effective comparison amount: 'included' quotes as 0, 'on_request' as
// unpriceable (sorts last so a priced row always beats it).
function effectiveAmount(row: QuotablePriceRow) {
    if (row.kind === 'included')
        return 0
    if (row.kind === 'fixed')
        return row.amount ?? Number.POSITIVE_INFINITY
    return Number.POSITIVE_INFINITY
}

// Per-vehicle rows refine the flat price: the cheapest single vehicle that
// seats the whole group wins, then the ANY_VEHICLE row covers everything else
// the fleet can seat in one go. Groups no single vehicle fits split across
// identical vehicles — quantity × per-vehicle price, cheapest total first,
// fewer vehicles on ties — with the flat row splitting by the largest active
// vehicle's capacity ("10 people, biggest van seats 8" → 2 × the flat price).
// With no vehicle types defined there is no declared capacity: flat rows
// quote any group size as one vehicle.
function resolvePriceRow(catalog: QuoteCatalog, routeId: number, tripType: 'one_way' | 'round_trip', date: string, passengers: number) {
    const rows = catalog.routePrices.filter(r => r.routeId === routeId && r.tripType === tripType)
    if (!rows.length)
        return null

    const vehicles = catalog.vehicleTypes.filter(v => v.active)
    const fleetCapacity = vehicles.reduce((max, v) => Math.max(max, v.maxPassengers), 0)
    const vehicleRow = (vehicleTypeId: number) => resolveSeasonalRow(rows.filter(r => r.vehicleTypeId === vehicleTypeId), date)

    const fitting = vehicles
        .filter(v => v.maxPassengers >= passengers)
        .map(v => vehicleRow(v.id))
        .filter(row => row !== null)
    if (fitting.length) {
        return { row: fitting.sort((a, b) => effectiveAmount(a) - effectiveAmount(b))[0]!, quantity: 1 }
    }

    const flat = vehicleRow(ANY_VEHICLE)
    if (flat && (!fleetCapacity || passengers <= fleetCapacity)) {
        return { row: flat, quantity: 1 }
    }

    const splits: { row: QuotablePriceRow, quantity: number }[] = vehicles
        .map(v => ({ row: vehicleRow(v.id), quantity: Math.ceil(passengers / v.maxPassengers) }))
        .filter((split): split is { row: QuotablePriceRow, quantity: number } => split.row !== null)
    if (flat && fleetCapacity) {
        splits.push({ row: flat, quantity: Math.ceil(passengers / fleetCapacity) })
    }
    if (!splits.length)
        return null
    return splits.sort((a, b) =>
        effectiveAmount(a.row) * a.quantity - effectiveAmount(b.row) * b.quantity || a.quantity - b.quantity,
    )[0]!
}

export function quoteTransfer(catalog: QuoteCatalog, input: QuoteInput): TransferQuote | null {
    const match = matchTransferRoute(catalog, input.fromPlaceId, input.toPlaceId)
    if (!match)
        return null

    const buildQuote = (resolved: { row: QuotablePriceRow, quantity: number }, derivedRoundTrip: boolean): TransferQuote => {
        const { row, quantity } = resolved
        const base = row.kind === 'fixed' ? row.amount : row.kind === 'included' ? 0 : null
        const unitAmount = base === null ? null : derivedRoundTrip ? base * 2 : base
        return {
            routeId: match.route.id,
            priceId: row.id,
            reversed: match.reversed,
            tripType: input.tripType,
            kind: row.kind,
            quantity,
            unitAmount,
            amount: unitAmount === null ? null : unitAmount * quantity,
            vehicleTypeId: row.vehicleTypeId,
            derivedRoundTrip,
        }
    }

    const resolved = resolvePriceRow(catalog, match.route.id, input.tripType, input.date, input.passengers)
    if (resolved)
        return buildQuote(resolved, false)

    // No explicit round_trip row: a round trip quotes as 2× the one-way row.
    // The inverse never derives — a round-trip-only product (tour transfers)
    // has no one-way price.
    if (input.tripType === 'round_trip') {
        const oneWay = resolvePriceRow(catalog, match.route.id, 'one_way', input.date, input.passengers)
        if (oneWay)
            return buildQuote(oneWay, true)
    }
    return null
}

// What the dashboard stores as computedTotal: a number only when the catalog
// commits to one ('included' counts as 0), null when the quote is manual.
export function quoteTotal(quote: TransferQuote | null): number | null {
    if (!quote)
        return null
    return quote.kind === 'on_request' ? null : quote.amount
}
